import React, { forwardRef, type ReactElement, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Popup, { type PopupComponentProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { CUSTOM_OPTION_VALUE } from '../../../constants/filters/types';
import { FilterButton } from '../filter-button';
import { FilterButtonOld } from '../filter-button-old';
import { MaybeHoverOutWrapper } from '../hover-out-wrapper';
import { type SelectFilterOption } from '../select-filter';
import { type CustomPopperModifier, matchReferenceWidthModifier } from '../utils/popper';

import DateOption from './date-option';
import DateRangePicker from './date-range-picker';
import { i18n } from './messages';

const containerStyles = xcss({
	boxSizing: 'border-box',
	width: '375px',
	overflow: 'visible',
	backgroundColor: 'elevation.surface.overlay',
	':focus': {
		outline: 'none',
	},
	borderRadius: 'radius.small',
	boxShadow: 'elevation.shadow.overlay',
	zIndex: 'card',
});

const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => (
	<Box
		{...props}
		paddingBlock="space.100"
		ref={ref}
		xcss={containerStyles}
		data-testid={undefined}
	/>
));

const formatDate = (dateString: string, locale: string) => {
	// to keep date string in local time, not UTC:
	// https://stackoverflow.com/questions/39223481/incorrect-date-shown-in-new-date-in-javascript
	const modifiedDateString = dateString.concat('T00:00');
	return new Date(modifiedDateString).toLocaleDateString(locale);
};

const createDatePickerLabel = (
	locale: string,
	afterString: string,
	beforeString: string,
	from?: string,
	to?: string,
) => {
	if (from && !to) {
		const localeFromDate = formatDate(from, locale);
		return `${afterString} ${localeFromDate}`;
	}

	if (!from && to) {
		const localeToDate = formatDate(to, locale);
		return `${beforeString} ${localeToDate}`;
	}

	if (from && to) {
		const localeFromDate = formatDate(from, locale);
		const localeToDate = formatDate(to, locale);
		return `${localeFromDate} - ${localeToDate}`;
	}
};

const getButtonLabel = (
	isSelected: boolean,
	isCustomDateSelected: boolean,
	selectedLabel: string,
	label: string,
	afterString: string,
	beforeString: string,
	locale: string,
	from?: string,
	to?: string,
) => {
	if (!isSelected) {
		return label;
	}

	const prefix = label.length > 0 ? `${label}: ` : '';

	if (isCustomDateSelected && (from || to)) {
		const valueLabel = createDatePickerLabel(locale, afterString, beforeString, from, to);
		return `${prefix}${valueLabel}`;
	}

	return `${prefix}${selectedLabel}`;
};

type DateFilterWithCustomRangePickerProps = {
	options: SelectFilterOption[];
	onDropdownOpen?: () => void;
	onSelectChange: (option: SelectFilterOption, customValue: Record<'from' | 'to', string>) => void;
	selectedOptions: SelectFilterOption[];
	customDateRange: Record<'from' | 'to', string>;
	label: string;
	testId?: string;
	isFilterInDropdown?: boolean;
	isDefaultOpen?: boolean;
	isFiltersDiscoverability2Enabled?: boolean;
	iconBefore?: ReactElement | null;
	hideOnHoverOut?: boolean;
	matchReferenceWidth?: boolean;
	contentFit?: 'default' | 'compact';
};

const DateFilterWithCustomRangePicker = ({
	options,
	onDropdownOpen,
	onSelectChange,
	selectedOptions,
	customDateRange,
	label,
	testId,
	isFilterInDropdown,
	isDefaultOpen = false,
	isFiltersDiscoverability2Enabled,
	iconBefore,
	hideOnHoverOut,
	matchReferenceWidth = isFilterInDropdown,
	contentFit,
}: DateFilterWithCustomRangePickerProps) => {
	const [open, setOpen] = useState(isDefaultOpen);
	const [customDateOpen, setCustomDateOpen] = useState(false);
	const { formatMessage, locale } = useIntl();
	const [focusedOption, setFocusedOption] = useState(0);
	const [mouseHovered, setMouseHovered] = useState(false);
	const buttonRef = useRef<HTMLButtonElement | null>(null);

	const isSelected = selectedOptions.length > 0 || open;
	const firstSelected = selectedOptions[0];

	const isCustomDateSelected = firstSelected?.value === CUSTOM_OPTION_VALUE;

	const customModifiers: CustomPopperModifier[] = [
		...(matchReferenceWidth ? [matchReferenceWidthModifier] : []),
	];

	const labelToDisplay = getButtonLabel(
		isSelected,
		isCustomDateSelected,
		firstSelected?.label || '',
		label,
		formatMessage(i18n.dateFilterAfterLabel),
		formatMessage(i18n.dateFilterBeforeLabel),
		locale,
		customDateRange.from,
		customDateRange.to,
	);

	const handleOptionClick = (option: SelectFilterOption) => {
		if (option.value === CUSTOM_OPTION_VALUE) {
			setCustomDateOpen(true);
		} else {
			setCustomDateOpen(false);
			onSelectChange(option, customDateRange);
			setOpen(false);
		}
	};

	const handleCustomDateRangeChange = (from: string, to: string) => {
		const option = options.find((o) => o.value === CUSTOM_OPTION_VALUE);
		if (!option) {
			return;
		}
		onSelectChange(option, { from, to });
		setOpen(false);
	};

	const handleClickDropdownBtn = () => {
		if (!open && onDropdownOpen) {
			onDropdownOpen();
		}
		setOpen(!open);
		setFocusedOption(0);
		buttonRef.current?.focus();
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setFocusedOption((prev) => (prev + 1) % options.length);
		}
		if (event.key === 'ArrowUp') {
			event.preventDefault();
			setFocusedOption((prev) => (prev - 1 + options.length) % options.length);
		}
		if (event.key === 'Enter' && open) {
			event.preventDefault();
			handleOptionClick(options[focusedOption]);
		}
	};

	useEffect(() => {
		if (isCustomDateSelected) {
			setCustomDateOpen(true);
		} else {
			setCustomDateOpen(false);
		}
	}, [isCustomDateSelected]);

	return (
		<MaybeHoverOutWrapper enabled={hideOnHoverOut} callback={() => setOpen(false)}>
			<Popup
				placement="bottom-start"
				onClose={() => setOpen(false)}
				isOpen={open}
				autoFocus={false}
				popupComponent={PopupComponent}
				shouldRenderToParent
				content={() => (
					<Box onMouseOver={() => setMouseHovered(true)} onMouseOut={() => setMouseHovered(false)}>
						{options.map((option, index) => (
							<DateOption
								hovered={!mouseHovered && focusedOption === index}
								key={option.value}
								option={option}
								onClick={() => handleOptionClick(option)}
							/>
						))}
						{customDateOpen && (
							<DateRangePicker
								onCustomDateRangeChange={handleCustomDateRangeChange}
								customDateRange={customDateRange}
							/>
						)}
					</Box>
				)}
				trigger={(triggerProps) =>
					isFiltersDiscoverability2Enabled ? (
						<FilterButton
							{...triggerProps}
							contentFit={contentFit}
							testId={testId ? `${testId}-button` : undefined}
							isSelected={isSelected}
							onClick={handleClickDropdownBtn}
							onKeyDown={handleKeyDown}
							filterOrControlId="lastUpdated"
							isFilterInDropdown={isFilterInDropdown}
							iconBefore={iconBefore}
						>
							{labelToDisplay}
						</FilterButton>
					) : (
						<FilterButtonOld
							label={labelToDisplay}
							filterOrControlId={'lastUpdated'}
							otherProps={triggerProps}
							isSelected={isSelected}
							onClick={handleClickDropdownBtn}
							testId={testId ? `${testId}-button` : undefined}
							onKeyDown={handleKeyDown}
							wide
							iconBefore={iconBefore}
						/>
					)
				}
				modifiers={customModifiers}
			/>
		</MaybeHoverOutWrapper>
	);
};

export default DateFilterWithCustomRangePicker;
